'use client';

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useRouter, useSelectedLayoutSegment } from 'next/navigation';
import { motion, useReducedMotion } from 'motion/react';
import { STORY_TITLE_ID, getNeighborDays } from '../../_const/stories';
import { DIALOG_CLASS, StoryDialogControls } from './story-dialog-controls';
import {
  getStoryBackdrop,
  markStoryRendered,
  switchStory,
  useOpeningStory,
  warmStoryPoster,
  type StoryDirection,
} from '../../_utils/story-transition';

/** 가로채기 layout 의 모달 틀 — [day] 가 바뀌어도 재마운트되지 않아 dialog·포커스가 유지된다 */
export default function StoryDialog({
  stories,
  children,
}: {
  // 공개된 스토리(Day 순)와 포스터
  stories: { day: number; poster: string }[];
  children: ReactNode;
}) {
  const router = useRouter();
  const reduceMotion = useReducedMotion();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [closing, setClosing] = useState(false);
  const day = Number(useSelectedLayoutSegment());

  // 연 요소(카드)는 렌더 단계에서 잡는다 — 효과에서 잡으면 StrictMode 재실행 때 이미 옮겨 간 포커스를 잡는다
  const [opener] = useState(() =>
    typeof document === 'undefined'
      ? null
      : (document.activeElement as HTMLElement | null),
  );
  // 임시 모달이 떠 있었으면 진입 모션 생략. 렌더 단계에서 읽어야 임시 모달의 unmount 정리보다 먼저다
  const opening = useOpeningStory();
  const [alreadyShown] = useState(opening !== null);

  // showModal 이 포커스 트랩·Escape·배경 inert 를 맡는다. 전환이 새 화면을 찍기 전에 열려 있도록 layout effect
  useLayoutEffect(
    function openAsModalAndLockScroll() {
      const dialog = dialogRef.current;
      const { overflow } = document.documentElement.style;
      if (dialog && !dialog.open) dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      return function unlockScrollAndRestoreFocus() {
        document.documentElement.style.overflow = overflow;
        // 모달이 열려 있으면 바깥이 inert 라 포커스가 안 옮겨진다 — 먼저 닫는다
        dialog?.close();
        opener?.focus();
      };
    },
    [opener],
  );

  useLayoutEffect(
    function scrollToTopOnStoryChange() {
      dialogRef.current?.scrollTo({ top: 0 });
    },
    [day],
  );

  const releasedDays = stories.map(story => story.day);
  const { prev, next } = getNeighborDays(releasedDays, day);
  // 넘기기 전에 이웃 라우트와 포스터를 받아 둔다 — 디코딩된 포스터가 이전/다음 스냅샷의 밑깔개가 된다
  useEffect(
    function prepareNeighborStories() {
      for (const neighbor of stories) {
        if (neighbor.day !== prev && neighbor.day !== next) continue;
        router.prefetch(`/10years/story/${neighbor.day}`);
        warmStoryPoster(neighbor.poster);
      }
    },
    [router, stories, prev, next],
  );

  const close = () => router.back();
  const requestClose = () => {
    if (reduceMotion) close();
    else setClosing(true); // 퇴장 모션이 끝나면 onAnimationComplete 가 닫는다
  };

  // 마지막으로 요청한 Day 기준으로 넘긴다 — 그려진 Day 기준이면 연타 때 같은 곳을 다시 요청해 클릭이 사라진다
  const requestedDay = useRef(day);
  // replace — 넘긴 스토리가 히스토리에 쌓이지 않게
  const navigate = (direction: StoryDirection) => {
    const target = getNeighborDays(releasedDays, requestedDay.current)[
      direction
    ];
    if (target === undefined) return;
    requestedDay.current = target;
    switchStory(direction, function replaceStory() {
      router.replace(`/10years/story/${target}`, { scroll: false });
    });
  };

  const skipEnter = reduceMotion || alreadyShown;

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={STORY_TITLE_ID}
      onCancel={function closeWithMotion(e) {
        e.preventDefault(); // Escape 도 퇴장 모션을 거친다
        requestClose();
      }}
      className={DIALOG_CLASS.scroller}
    >
      <motion.div
        aria-hidden
        onClick={requestClose}
        className={DIALOG_CLASS.backdrop}
        initial={skipEnter ? false : { opacity: 0 }}
        animate={{ opacity: closing ? 0 : 1 }}
        transition={{ duration: 0.2 }}
      />
      <motion.div
        className={DIALOG_CLASS.panel}
        initial={skipEnter ? false : { opacity: 0, y: 24 }}
        animate={closing ? { opacity: 0, y: 24 } : { opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        onAnimationComplete={() => closing && close()}
      >
        <StoryDialogControls
          onClose={requestClose}
          onPrev={() => navigate('prev')}
          onNext={() => navigate('next')}
          prevDisabled={prev === undefined}
          nextDisabled={next === undefined}
        />
        {children}
      </motion.div>
    </dialog>
  );
}
